import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RadioTower, ArrowRightLeft, Lock, LockOpen, Undo2, Play, Pause, CheckCircle2, RefreshCw, GraduationCap, X } from "lucide-react";

const TUTORIAL_KEY = "mara_ref_court_tutorial_v1";

const TUTORIAL_STEPS = [
  {
    title: "Step 1 · Take Control",
    body: "Tap the yellow ‘Take Control’ button above the score grid to become the active scorer on this court. Only one official can score at a time.",
  },
  {
    title: "Step 2 · Score Points",
    body: "Tap either team panel to add one point. The engine drives 0 → 15 → 30 → 40 → Game automatically, with Deuce / Advantage / Tiebreak all handled. Use Undo to reverse the last point. Standings auto-recalculate.",
  },
  {
    title: "Step 3 · Transfer Handoff",
    body: "Need a break? Tap another official’s name under ‘Transfer to another official’ to hand over scoring rights instantly. Only the current scorer or a Super Admin can transfer.",
  },
];

/**
 * /referee/court/:court — Dedicated court-scoped scoring interface with the
 * two-referee "Transfer Scoring Control" handoff. Only the `active_scorer_id`
 * on the current match can write; Super Admin can override.
 */
export default function RefereeCourt() {
  const { court } = useParams();
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [refs, setRefs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tennis, setTennis] = useState(null); // { state, display, set_scores, totals }
  const [tie, setTie] = useState(null); // full tie payload with 3 matches + order

  useEffect(() => {
    try {
      if (!localStorage.getItem(TUTORIAL_KEY)) setShowTutorial(true);
    } catch { /* localStorage blocked */ }
  }, []);

  const closeTutorial = (persist = true) => {
    setShowTutorial(false);
    setTutorialStep(0);
    if (persist) {
      try { localStorage.setItem(TUTORIAL_KEY, "1"); } catch { /* ignore */ }
    }
  };
  const openTutorial = () => { setTutorialStep(0); setShowTutorial(true); };

  const load = async () => {
    try {
      const [{ data: ms }, { data: users }, { data: cfg }] = await Promise.all([
        api.get("/matches"),
        api.get("/users?role=court_umpire").catch(() => ({ data: [] })),
        api.get("/config/tie-match-order").catch(() => ({ data: { tie_match_order: [2, 1, 3] } })),
      ]);
      const order = cfg?.tie_match_order || [2, 1, 3];
      const courtMatches = (ms || []).filter((m) => m.court === court && ["scheduled", "live", "paused"].includes(m.status));
      // Season 3 mandate: within a tie, present matches in the tenant's play order.
      // Prefer LIVE first regardless of order, then apply the mandated seed order.
      const rank = Object.fromEntries(order.map((s, i) => [s, i]));
      const statusRank = { live: 0, paused: 1, scheduled: 2 };
      courtMatches.sort((a, b) => {
        const s = (statusRank[a.status] ?? 3) - (statusRank[b.status] ?? 3);
        if (s !== 0) return s;
        // Same status → the seed that plays first in the order wins.
        const sa = rank[a.seed] ?? 99;
        const sb = rank[b.seed] ?? 99;
        if (sa !== sb) return sa - sb;
        const fa = a.format_type === "set_based" ? 0 : 1;
        const fb = b.format_type === "set_based" ? 0 : 1;
        return fa - fb;
      });
      setMatches(courtMatches);
      setRefs(users || []);
      const top = courtMatches[0];
      if (top?.tie_id) {
        try {
          const { data: tieData } = await api.get(`/public/ties/${top.tie_id}`);
          setTie(tieData);
        } catch { setTie(null); }
      } else {
        setTie(null);
      }
      if (top && top.format_type === "set_based") {
        try {
          const { data } = await api.get(`/matches/${top.id}/tennis/state`);
          setTennis(data);
        } catch { setTennis(null); }
      } else {
        setTennis(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load court state");
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, [court]);

  const activeMatch = matches[0];
  const isSetBased = activeMatch?.format_type === "set_based";
  const iAmActive = activeMatch?.active_scorer_id === user?.id;
  const isSuper = user?.role === "super_admin";
  const canWrite = iAmActive || isSuper || !activeMatch?.active_scorer_id;

  const transferTo = async (uid) => {
    if (!activeMatch) return;
    setBusy(true);
    try {
      await api.post(`/matches/${activeMatch.id}/scoring-control`, { to_user_id: uid || null });
      toast.success(uid ? "Scoring control transferred" : "Scoring control released");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed");
    } finally { setBusy(false); }
  };

  const scoreTennisPoint = async (side) => {
    if (!activeMatch || !canWrite) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/matches/${activeMatch.id}/tennis/point`, { team: side });
      setTennis(data);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Score failed");
    } finally { setBusy(false); }
  };

  const undoTennisPoint = async () => {
    if (!activeMatch || !canWrite) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/matches/${activeMatch.id}/tennis/undo`);
      setTennis(data);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    } finally { setBusy(false); }
  };

  const scorePoint = async (side) => {
    if (!activeMatch || !canWrite) return;
    // Route to the tennis engine when set-based; fall through to legacy /point for time-based matches.
    if (isSetBased) return scoreTennisPoint(side);
    setBusy(true);
    try { await api.post(`/matches/${activeMatch.id}/point`, { side }); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Score failed"); }
    finally { setBusy(false); }
  };

  const doAction = async (path) => {
    if (!activeMatch) return;
    setBusy(true);
    try { await api.post(`/matches/${activeMatch.id}/${path}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-6" data-testid="referee-court-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RadioTower className="w-5 h-5 text-[#F4B400]" />
            <div>
              <h1 className="font-anton text-4xl uppercase tracking-tight">Referee · {court}</h1>
              <p className="text-xs text-zinc-500">Court-scoped scoring · Transfer Control between two officials.</p>
            </div>
          </div>
          <Link to="/referee" className="text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white">← All courts</Link>
        </div>

        {tie && (tie.matches?.length || 0) > 1 && (
          <TieProgressBar tie={tie} activeMatchId={activeMatch?.id} />
        )}

        <div className="mb-4 flex justify-end">
          <button
            onClick={openTutorial}
            data-testid="referee-tutorial-open-btn"
            className="text-[11px] uppercase tracking-widest text-zinc-400 hover:text-[#F4B400] flex items-center gap-1"
          >
            <GraduationCap className="w-3.5 h-3.5" /> How this works
          </button>
        </div>

        {showTutorial && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="referee-tutorial-overlay">
            <Card className="w-full max-w-lg bg-[#0f1a15] border-[#F4B400]/40 p-6 relative">
              <button
                onClick={() => closeTutorial(true)}
                aria-label="Close tutorial"
                data-testid="referee-tutorial-close-btn"
                className="absolute top-3 right-3 text-zinc-500 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2 mb-4">
                <GraduationCap className="w-5 h-5 text-[#F4B400]" />
                <div className="text-[10px] uppercase tracking-widest text-[#F4B400] font-bold">
                  Referee Onboarding · {tutorialStep + 1} of {TUTORIAL_STEPS.length}
                </div>
              </div>
              <div className="font-anton text-2xl uppercase tracking-tight mb-3" data-testid={`referee-tutorial-step-${tutorialStep}`}>
                {TUTORIAL_STEPS[tutorialStep].title}
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed mb-6">
                {TUTORIAL_STEPS[tutorialStep].body}
              </p>
              <div className="flex gap-1 mb-4">
                {TUTORIAL_STEPS.map((_, i) => (
                  <div
                    key={i}
                    className={`flex-1 h-1 rounded transition-all ${i <= tutorialStep ? "bg-[#F4B400]" : "bg-white/10"}`}
                  />
                ))}
              </div>
              <div className="flex items-center justify-between gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => closeTutorial(true)}
                  data-testid="referee-tutorial-skip-btn"
                  className="border-white/20 text-zinc-400 hover:text-white"
                >
                  Skip
                </Button>
                <div className="flex gap-2">
                  {tutorialStep > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setTutorialStep((s) => Math.max(0, s - 1))}
                      data-testid="referee-tutorial-back-btn"
                      className="border-white/20 text-zinc-300"
                    >
                      Back
                    </Button>
                  )}
                  {tutorialStep < TUTORIAL_STEPS.length - 1 ? (
                    <Button
                      size="sm"
                      onClick={() => setTutorialStep((s) => s + 1)}
                      data-testid="referee-tutorial-next-btn"
                      className="bg-[#F4B400] text-black font-bold uppercase tracking-widest hover:bg-[#e5aa00]"
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => closeTutorial(true)}
                      data-testid="referee-tutorial-done-btn"
                      className="bg-[#00A86B] text-black font-bold uppercase tracking-widest hover:bg-[#00c17b]"
                    >
                      Got it
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          </div>
        )}

        {!activeMatch ? (
          <Card className="p-12 text-center border-white/10 bg-[#0f1a15]" data-testid="referee-court-empty">
            <div className="text-2xl text-zinc-500 uppercase tracking-widest">No live or scheduled match on {court}</div>
          </Card>
        ) : (
          <>
            <Card className={`p-4 mb-4 border ${canWrite ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`} data-testid="scoring-control-banner">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {canWrite ? <LockOpen className="w-4 h-4 text-emerald-400" /> : <Lock className="w-4 h-4 text-red-400" />}
                  <div>
                    <div className="font-bold uppercase tracking-widest text-xs">
                      {iAmActive ? "You have scoring control" : activeMatch.active_scorer_id ? "Another official is scoring" : "Control not yet assigned"}
                    </div>
                    <div className="text-[10px] text-zinc-500">
                      {activeMatch.active_scorer_id ? `Held by user ${activeMatch.active_scorer_id.slice(0, 8)}` : "Tap Take Control to grab the pen."}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => transferTo(user.id)}
                    disabled={busy || iAmActive || (activeMatch.active_scorer_id && !isSuper)}
                    className="bg-[#F4B400] text-black font-bold uppercase tracking-widest hover:bg-[#e5aa00] disabled:opacity-40"
                    data-testid="scoring-take-control-btn">
                    <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Take Control
                  </Button>
                  {iAmActive && (
                    <Button size="sm" variant="outline" onClick={() => transferTo(null)}
                      disabled={busy} className="border-white/20 text-zinc-300" data-testid="scoring-release-btn">Release</Button>
                  )}
                </div>
              </div>
              {refs.length > 0 && iAmActive && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Transfer to another official</div>
                  <div className="flex flex-wrap gap-2">
                    {refs.filter((r) => r.id !== user?.id).map((r) => (
                      <button key={r.id} onClick={() => transferTo(r.id)} disabled={busy}
                        data-testid={`scoring-transfer-to-${r.id}`}
                        className="text-[11px] uppercase tracking-widest px-3 py-1.5 rounded border border-white/10 bg-black/30 hover:border-[#F4B400]/40 text-zinc-300 hover:text-white">
                        {r.name || r.email}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {isSetBased && tennis ? (
              <TennisScoreboard
                match={activeMatch}
                tennis={tennis}
                canWrite={canWrite}
                busy={busy}
                onScore={scoreTennisPoint}
              />
            ) : (
              <div className="grid grid-cols-2 gap-4 mb-4" data-testid="referee-score-grid">
                <ScoreSide label={activeMatch.team_a_name || "Team A"}
                  score={activeMatch.total_games_won_a ?? activeMatch.score_a ?? 0}
                  disabled={!canWrite || busy} onScore={() => scorePoint("a")} testId="score-team-a" />
                <ScoreSide label={activeMatch.team_b_name || "Team B"}
                  score={activeMatch.total_games_won_b ?? activeMatch.score_b ?? 0}
                  disabled={!canWrite || busy} onScore={() => scorePoint("b")} testId="score-team-b" />
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <BigBtn label="Undo" icon={Undo2}
                onClick={isSetBased ? undoTennisPoint : () => doAction("undo")}
                disabled={!canWrite || busy} testId="referee-undo-btn" />
              <BigBtn label={activeMatch.status === "live" ? "Pause" : "Start"} icon={activeMatch.status === "live" ? Pause : Play}
                onClick={() => doAction(activeMatch.status === "live" ? "pause" : "start")} disabled={!canWrite || busy} testId="referee-play-pause-btn" />
              <BigBtn label="Complete" icon={CheckCircle2} onClick={() => doAction("finalize")} disabled={!canWrite || busy} testId="referee-finalize-btn" />
              <BigBtn label="Refresh" icon={RefreshCw} onClick={load} disabled={busy} testId="referee-refresh-btn" />
            </div>

            <div className="mt-6 text-center text-[10px] uppercase tracking-widest text-zinc-500">
              Match {activeMatch.id.slice(0, 8)} · {activeMatch.stage || "group"} · {activeMatch.status}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TennisScoreboard({ match, tennis, canWrite, busy, onScore }) {
  const disp = tennis?.display || { a: "0", b: "0", label: "" };
  const state = tennis?.state || {};
  const setScores = tennis?.set_scores || [];
  const totals = tennis?.totals || {};
  const inTiebreak = !!state.in_tiebreak;
  const isDeuce = !!state.deuce && !state.advantage;

  return (
    <div className="mb-4" data-testid="referee-tennis-scoreboard">
      {/* Big current-game status banner */}
      <div className="text-center mb-3">
        {inTiebreak && (
          <div className="inline-block px-3 py-1 rounded-full bg-[#F4B400] text-black text-[10px] font-bold uppercase tracking-widest" data-testid="tennis-tiebreak-badge">
            Tiebreak · First to 7 · Win by 2
          </div>
        )}
        {isDeuce && !inTiebreak && (
          <div className="inline-block px-3 py-1 rounded-full bg-[#e32424] text-white text-[10px] font-bold uppercase tracking-widest" data-testid="tennis-deuce-badge">Deuce</div>
        )}
        {disp.label && disp.label.startsWith("ADVANTAGE") && (
          <div className="inline-block px-3 py-1 rounded-full bg-[#00A86B] text-black text-[10px] font-bold uppercase tracking-widest" data-testid="tennis-advantage-badge">{disp.label}</div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <TennisSide
          label={match.team_a_name || "Team A"}
          gameScore={disp.a}
          sets={setScores.map((s) => s.a)}
          setsWon={state.sets_won_a || 0}
          currentGames={state.current_set_games_a || 0}
          disabled={!canWrite || busy}
          onScore={() => onScore("a")}
          testId="tennis-score-team-a"
        />
        <TennisSide
          label={match.team_b_name || "Team B"}
          gameScore={disp.b}
          sets={setScores.map((s) => s.b)}
          setsWon={state.sets_won_b || 0}
          currentGames={state.current_set_games_b || 0}
          disabled={!canWrite || busy}
          onScore={() => onScore("b")}
          testId="tennis-score-team-b"
        />
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div className="rounded border border-white/10 bg-black/40 py-2">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">Total Games</div>
          <div className="font-anton text-2xl tabular-nums">
            {totals.total_games_won_a ?? 0} <span className="text-zinc-600">–</span> {totals.total_games_won_b ?? 0}
          </div>
        </div>
        <div className="rounded border border-white/10 bg-black/40 py-2">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">Sets</div>
          <div className="font-anton text-2xl tabular-nums">
            {state.sets_won_a ?? 0} <span className="text-zinc-600">–</span> {state.sets_won_b ?? 0}
          </div>
        </div>
        <div className="rounded border border-white/10 bg-black/40 py-2">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">Game Diff</div>
          <div className="font-anton text-2xl tabular-nums">
            {(totals.total_games_won_a ?? 0) - (totals.total_games_won_b ?? 0) >= 0 ? "+" : ""}
            {(totals.total_games_won_a ?? 0) - (totals.total_games_won_b ?? 0)}
          </div>
        </div>
      </div>
    </div>
  );
}

function TennisSide({ label, gameScore, sets, setsWon, currentGames, disabled, onScore, testId }) {
  return (
    <button onClick={onScore} disabled={disabled} data-testid={testId}
      className={`p-4 rounded-2xl border transition-all min-h-[260px] flex flex-col justify-between ${
        disabled ? "border-white/10 bg-white/5 cursor-not-allowed opacity-60"
                 : "border-[#F4B400]/30 bg-[#F4B400]/5 hover:border-[#F4B400] hover:bg-[#F4B400]/10 active:scale-95"
      }`}>
      <div>
        <div className="text-base font-bold uppercase tracking-widest text-zinc-300 mb-1 truncate">{label}</div>
        <div className="flex items-center gap-3 mb-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Sets Won</div>
          <div className="font-anton text-xl tabular-nums text-emerald-400" data-testid={`${testId}-sets-won`}>{setsWon}</div>
        </div>
        <div className="flex items-center gap-2 mb-2">
          {sets.map((n, i) => (
            <div key={i} className="text-xs uppercase tracking-widest bg-black/40 border border-white/10 rounded px-2 py-1">
              <span className="text-zinc-500">S{i + 1}</span>{" "}
              <span className="font-anton tabular-nums">{n}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Current Set · Games {currentGames}</div>
        <div className="font-anton text-[120px] leading-none tabular-nums text-[#F4B400]" data-testid={`${testId}-game`}>
          {gameScore}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Tap to score +1 point</div>
      </div>
    </button>
  );
}

function ScoreSide({ label, score, disabled, onScore, testId }) {
  return (
    <button onClick={onScore} disabled={disabled} data-testid={testId}
      className={`p-6 rounded-2xl border transition-all min-h-[220px] ${
        disabled ? "border-white/10 bg-white/5 cursor-not-allowed opacity-60"
                 : "border-[#F4B400]/30 bg-[#F4B400]/5 hover:border-[#F4B400] hover:bg-[#F4B400]/10 active:scale-95"
      }`}>
      <div className="text-lg font-bold uppercase tracking-widest text-zinc-300 mb-2 truncate">{label}</div>
      <div className="font-anton text-[120px] leading-none tabular-nums text-[#F4B400]">{score}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">Tap to score +1</div>
    </button>
  );
}

function BigBtn({ label, icon: Icon, onClick, disabled, testId }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testId}
      className={`h-20 rounded-xl border flex flex-col items-center justify-center gap-1 transition ${
        disabled ? "border-white/5 bg-white/5 opacity-40" : "border-white/10 bg-black/40 hover:border-[#F4B400]/40 active:scale-95"
      }`}>
      <Icon className="w-5 h-5" />
      <span className="text-[11px] uppercase tracking-widest font-bold">{label}</span>
    </button>
  );
}


function TieProgressBar({ tie, activeMatchId }) {
  const matches = tie.matches || [];
  const total = matches.length;
  const finished = matches.filter((m) => (m.status || "").toLowerCase() === "finished").length;
  const upNext = matches.find((m) => (m.status || "").toLowerCase() !== "finished");
  const teamAName = tie.team_a?.name || "Team A";
  const teamBName = tie.team_b?.name || "Team B";
  const gd = tie.game_difference ?? 0;
  const leader = gd > 0 ? teamAName : gd < 0 ? teamBName : "Level";
  const gdAbs = Math.abs(gd);
  return (
    <div className="mb-4 p-4 rounded-lg border border-white/10 bg-[#0f1a15]" data-testid="referee-tie-progress">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <div className="text-sm font-bold uppercase tracking-widest">
          <span className="text-white/90">{teamAName}</span>
          <span className="text-zinc-500 mx-2">vs</span>
          <span className="text-white/90">{teamBName}</span>
        </div>
        <div className="text-[11px] uppercase tracking-widest text-zinc-400">
          <span data-testid="referee-tie-progress-count" className="text-[#F4B400] font-bold">{finished} of {total}</span> matches complete
          {upNext && (
            <>
              <span className="text-zinc-600 mx-2">·</span>
              <span data-testid="referee-tie-progress-upnext">Seed {upNext.seed} up next</span>
            </>
          )}
        </div>
      </div>
      <div className="flex gap-1 mb-2">
        {matches.map((m, i) => {
          const s = (m.status || "").toLowerCase();
          const active = m.id === activeMatchId;
          const cls = s === "finished"
            ? "bg-emerald-500"
            : active
            ? "bg-[#F4B400] animate-pulse"
            : "bg-white/10";
          return <div key={m.id} className={`flex-1 h-1.5 rounded ${cls}`} data-testid={`referee-tie-progress-slot-${i + 1}`} />;
        })}
      </div>
      <div className="flex flex-wrap items-center justify-between text-[10px] text-zinc-500 uppercase tracking-widest gap-2">
        <div>
          {matches.map((m, i) => (
            <span key={m.id} className="mr-3">
              <span className="text-zinc-600">M{i + 1}</span>{" "}
              <span className={`${(m.status || "").toLowerCase() === "finished" ? "text-emerald-400" : m.id === activeMatchId ? "text-[#F4B400]" : "text-white/50"}`}>Seed {m.seed}</span>
            </span>
          ))}
        </div>
        <div>
          Tie GD · <span className={`font-anton text-sm ${gd === 0 ? "text-zinc-400" : gd > 0 ? "text-emerald-400" : "text-red-400"}`}>{gd > 0 ? "+" : ""}{gd}</span>
          {gd !== 0 && <span className="text-zinc-500 ml-1">({leader} +{gdAbs})</span>}
        </div>
      </div>
    </div>
  );
}
